import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { PushNotifications } from '@capacitor/push-notifications'
import type {
  Token,
  PushNotificationSchema,
  ActionPerformed as PushNotificationActionPerformed,
} from '@capacitor/push-notifications'
import { LocalNotifications } from '@capacitor/local-notifications'
import type { ActionPerformed as LocalNotificationActionPerformed } from '@capacitor/local-notifications'
import { ApolloClient } from '@apollo/client'
import { ADD_DEVICE_TO_USER, UNLINK_DEVICE } from '../graphql/mutations/pushNotifications'
import type { AddDeviceToUserMutation, AddDeviceToUserMutationVariables } from '../types/graphql-generated'

interface RegisterOptions {
  apolloClient: ApolloClient
  onNotificationReceived?: (notification: PushNotificationSchema) => void
  onNotificationActionPerformed?: (
    notification: PushNotificationActionPerformed | LocalNotificationActionPerformed
  ) => void
  /**
   * Called with an in-app route when a tapped notification carries navigation
   * data (see `getNotificationRoute`). Typically wired to the Ionic router.
   */
  navigate?: (path: string) => void
}

let registrationInFlight: Promise<void> | null = null

/**
 * Shape of the `data` payload the backend attaches to push notifications.
 * The contract is defined in `app/services/push_notification_services/notification_data.rb`.
 */
export interface PushNotificationData {
  type?: string
  fantasy_league_id?: string
  [key: string]: unknown
}

// Navigation targets understood by the app. Must stay in sync with the backend
// `PushNotificationServices::NotificationData` constants.
const NOTIFICATION_TYPES = {
  RECEIVED_OFFERS: 'received_offers',
  OFFER_HISTORY: 'offer_history',
  LINEUP: 'lineup',
  MESSAGES: 'messages',
} as const

/**
 * Maps a push notification's `data` payload to an in-app route.
 * Returns null when the data does not describe a known navigation target.
 */
export function getNotificationRoute(
  data: PushNotificationData | undefined | null
): string | null {
  if (!data) return null

  const leagueId = data.fantasy_league_id
  if (!leagueId) return null

  switch (data.type) {
    case NOTIFICATION_TYPES.RECEIVED_OFFERS:
      return `/app/ligas/${leagueId}/mercado/recibidas`
    case NOTIFICATION_TYPES.OFFER_HISTORY:
      return `/app/ligas/${leagueId}/mercado/pasadas`
    case NOTIFICATION_TYPES.LINEUP:
      return `/app/ligas/${leagueId}/equipo?seccion=alineacion`
    case NOTIFICATION_TYPES.MESSAGES:
      return `/app/ligas/${leagueId}/mensajes`
    default:
      return null
  }
}

/**
 * Registers the device for push notifications and sends the token to the backend.
 * Only works on native platforms (iOS/Android).
 *
 * Foreground notifications: When the app is open, notifications are displayed using Local Notifications API.
 * Background notifications: When the app is closed/minimized, the native platform displays them automatically.
 */
export function registerPushNotifications(options: RegisterOptions): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return Promise.resolve()
  }

  if (registrationInFlight) return registrationInFlight

  registrationInFlight = performPushNotificationRegistration(options).finally(() => {
    registrationInFlight = null
  })

  return registrationInFlight
}

async function performPushNotificationRegistration({
  apolloClient,
  onNotificationReceived,
  onNotificationActionPerformed,
  navigate,
}: RegisterOptions): Promise<void> {
  try {
    // Request permission to use push notifications
    // iOS will prompt user, Android automatically grants permission
    const permResult = await PushNotifications.requestPermissions()

    if (permResult.receive !== 'granted') {
      return
    }

    // Remove any existing listeners before adding new ones.
    // Capacitor listeners are cumulative, so without this a second call (e.g.
    // React StrictMode in dev) would stack duplicate handlers.
    await PushNotifications.removeAllListeners()
    await LocalNotifications.removeAllListeners()

    const handleNotificationAction = (
      notification: PushNotificationActionPerformed | LocalNotificationActionPerformed,
      data: PushNotificationData | undefined
    ) => {
      const route = getNotificationRoute(data)
      if (route && navigate) {
        navigate(route)
      }

      if (onNotificationActionPerformed) {
        onNotificationActionPerformed(notification)
      }
    }

    // Set up listeners BEFORE calling register().
    // On iOS, the 'registration' event can fire immediately with a cached APNs token,
    // so the listener must be in place before register() is called or the event is missed.
    await PushNotifications.addListener('registration', async (token: Token) => {
      // Store token locally for logout unlinking
      localStorage.setItem('pushNotificationToken', token.value)

      // Send token to backend
      try {
        const platform = Capacitor.getPlatform() // 'ios' or 'android'
        const appInfo = await App.getInfo()
        const result = await apolloClient.mutate<AddDeviceToUserMutation, AddDeviceToUserMutationVariables>({
          mutation: ADD_DEVICE_TO_USER,
          variables: {
            input: {
              token: token.value,
              platform: platform,
              appVersion: appInfo.version,
            },
          },
        })
        if (!result.data?.addDeviceToUser?.success) {
          console.error(
            '[PushNotifications] Backend rejected token:',
            result.data?.addDeviceToUser?.errors
          )
        }
      } catch (err) {
        console.error('[PushNotifications] Mutation failed:', err)
      }
    })

    await PushNotifications.addListener('registrationError', (error) => {
      console.error('[PushNotifications] APNs registration error:', JSON.stringify(error))
    })

    // Handle notifications when app is in foreground
    // The system does NOT automatically display anything, so we must manually show a notification
    await PushNotifications.addListener(
      'pushNotificationReceived',
      async (notification: PushNotificationSchema) => {
        await LocalNotifications.schedule({
          notifications: [
            {
              id: Math.floor(Math.random() * 2147483647),
              title: notification.title || 'Nueva notificación',
              body: notification.body || '',
              extra: notification.data,
              sound: 'default',
            }
          ]
        })

        // Call user callback if provided
        if (onNotificationReceived) {
          onNotificationReceived(notification)
        }
      }
    )

    // Background push notification taps are reported by the push plugin.
    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (notification: PushNotificationActionPerformed) => {
        handleNotificationAction(
          notification,
          notification.notification.data as PushNotificationData
        )
      }
    )

    // Foreground pushes are displayed through the local notifications plugin,
    // whose tap event and payload field are different from the push plugin's.
    await LocalNotifications.addListener(
      'localNotificationActionPerformed',
      (notification: LocalNotificationActionPerformed) => {
        handleNotificationAction(
          notification,
          notification.notification.extra as PushNotificationData | undefined
        )
      }
    )

    // Register with Apple / Google to receive push via APNS/FCM
    await PushNotifications.register()

    // Request permission for local notifications (needed for foreground display)
    await LocalNotifications.requestPermissions()
  } catch (err) {
    console.error('[PushNotifications] Setup failed:', err)
  }
}

/**
 * Disables push notifications by unlinking the device token from the backend and
 * persisting the preference in localStorage. Throws on failure so the UI can revert.
 */
export async function disablePushNotifications(apolloClient: ApolloClient): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  const storedToken = localStorage.getItem('pushNotificationToken')
  if (storedToken) {
    // Backend call first — if it fails, throw so the toggle reverts
    await apolloClient.mutate({
      mutation: UNLINK_DEVICE,
      variables: { input: { token: storedToken } },
    })
    localStorage.removeItem('pushNotificationToken')
  }

  // Local cleanup only after backend succeeds
  localStorage.setItem('pushNotificationsDisabled', 'true')
  await PushNotifications.removeAllListeners()
  await LocalNotifications.removeAllListeners()
}

/**
 * Unregisters the device from push notifications by unlinking the token from the backend.
 * Should be called on logout.
 */
export async function unregisterPushNotifications(
  apolloClient: ApolloClient,
  token: string
): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return
  }

  try {
    await apolloClient.mutate({
      mutation: UNLINK_DEVICE,
      variables: {
        input: {
          token: token,
        },
      },
    })
  } catch {
    // Don't throw - unlinking is best-effort
  }
}

/**
 * Removes push state from this app installation without calling the backend.
 * Account deletion already destroys every DeviceToken through User's dependent
 * association, so another authenticated request would be both redundant and
 * unsafe while the deleted Auth0 access token is still cached.
 */
export async function cleanupPushNotificationsAfterAccountDeletion(): Promise<void> {
  localStorage.removeItem('pushNotificationToken')

  if (Capacitor.isNativePlatform()) {
    await PushNotifications.removeAllListeners()
    await LocalNotifications.removeAllListeners()
  }
}
